import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import type { Need, Service } from "@hub/shared";
import { Button, Pill, SectionTitle, toast } from "@/ui";
import { api, useEngineQuery, useSession } from "./_shims";
import { cardInvolvesContact } from "./lib/cards";
import { useConnections, useDeleteItem, useHubUserOf, useMatchCards, useSaveService } from "./lib/hooks";
import { CompleteNeedDialog } from "./components/CompleteNeedDialog";
import { ItemForm, type ItemKind } from "./components/ItemForm";
import { NeedRow, ServiceRow } from "./components/ItemRow";
import { MatchModal } from "./components/MatchModal";
import { MatchRow } from "./components/MatchRow";
import { useMatchOpener } from "./components/useMatchOpener";
import s from "./network.module.css";

function ConnectionStatus({ linkedUserId, name }: { linkedUserId: string; name: string }) {
  const { user, isSignedIn } = useSession();
  const qc = useQueryClient();
  const conns = useConnections(isSignedIn);
  const conn = conns.data?.connections.find((c) => c.otherUser.userId === linkedUserId);
  const request = useMutation({
    mutationFn: () => api.requestConnection({ toUserId: linkedUserId, connectionType: "contact_match" }),
    onSuccess: () => {
      toast(`Connection request sent to ${name}.`, "success");
      qc.invalidateQueries({ queryKey: ["network", "connections"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not send the request.", "danger"),
  });
  const respond = useMutation({
    mutationFn: (accept: boolean) => api.respondConnection(conn!.id, { accept }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["network", "connections"] }),
  });
  if (linkedUserId === user?.id) return null;
  if (!isSignedIn) return <Pill tone="info">On Hub</Pill>;
  if (!conn) {
    return (
      <div className="u-row">
        <Pill tone="info">On Hub</Pill>
        <Button size="sm" onClick={() => request.mutate()} loading={request.isPending}>
          Connect on Hub
        </Button>
      </div>
    );
  }
  if (conn.status === "accepted") return <Pill tone="success">Connected on Hub</Pill>;
  if (conn.status === "pending" && conn.toUserId === user?.id) {
    return (
      <div className="u-row">
        <Pill tone="warning">Wants to connect</Pill>
        <Button size="sm" variant="primary" onClick={() => respond.mutate(true)} loading={respond.isPending}>
          Accept
        </Button>
        <Button size="sm" variant="ghost" onClick={() => respond.mutate(false)}>
          Decline
        </Button>
      </div>
    );
  }
  if (conn.status === "pending") return <Pill tone="neutral">Request sent</Pill>;
  return <Pill tone="info">On Hub</Pill>;
}

/** This person's needs and services with inline add/edit/complete, the introductions they appear in, and their Hub status. */
export function ContactNetworkSection({ contactId, compact }: { contactId: string; compact?: boolean }) {
  const detail = useEngineQuery("getContact", { contactId });
  const hubUserId = useHubUserOf(contactId);
  const matches = useMatchCards();
  const opener = useMatchOpener();
  const [form, setForm] = useState<{ kind: ItemKind; need?: Need | null; service?: Service | null } | null>(null);
  const [completing, setCompleting] = useState<Need | null>(null);
  const delNeed = useDeleteItem("need");
  const delService = useDeleteItem("service");
  const saveService = useSaveService();

  if (!detail.data) return null;
  const { contact, profile, needs, services } = detail.data;
  const name = profile.displayName;
  const involved = (matches.data?.cards ?? []).filter((c) => cardInvolvesContact(c, contactId));
  const limit = compact ? 3 : Infinity;

  return (
    <div className="u-col" style={{ gap: 16 }} data-testid="contact-network-section">
      {hubUserId && (
        <div className="u-row">
          <ConnectionStatus linkedUserId={hubUserId} name={name} />
        </div>
      )}
      {contact.isPrivate && <div className={`${s.small} ${s.muted}`}>Private person: excluded from matching and sharing.</div>}

      <section>
        <SectionTitle
          action={
            <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => setForm({ kind: "need" })}>
              Add
            </Button>
          }
        >
          Needs
        </SectionTitle>
        {needs.length === 0 ? (
          <div className={`${s.small} ${s.muted}`}>Nothing recorded. What is {name} looking for?</div>
        ) : (
          <div className={s.stack}>
            {needs.slice(0, limit).map((n) => (
              <NeedRow key={n.id} need={n} onEdit={() => setForm({ kind: "need", need: n })} onComplete={() => setCompleting(n)} onDelete={() => window.confirm(`Delete the need "${n.title}"?`) && delNeed.mutate(n.id)} />
            ))}
            {needs.length > limit && <div className={`${s.small} ${s.muted}`}>+{needs.length - limit} more</div>}
          </div>
        )}
      </section>

      <section>
        <SectionTitle
          action={
            <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => setForm({ kind: "service" })}>
              Add
            </Button>
          }
        >
          Offers
        </SectionTitle>
        {services.length === 0 ? (
          <div className={`${s.small} ${s.muted}`}>Nothing recorded. What can {name} help with?</div>
        ) : (
          <div className={s.stack}>
            {services.slice(0, limit).map((sv) => (
              <ServiceRow key={sv.id} service={sv} onEdit={() => setForm({ kind: "service", service: sv })} onToggleActive={() => saveService.mutate({ ...sv, isActive: !sv.isActive })} onDelete={() => window.confirm(`Delete the offer "${sv.title}"?`) && delService.mutate(sv.id)} />
            ))}
            {services.length > limit && <div className={`${s.small} ${s.muted}`}>+{services.length - limit} more</div>}
          </div>
        )}
      </section>

      {involved.length > 0 && (
        <section>
          <SectionTitle action={<Pill tone="neutral">{involved.length}</Pill>}>Introductions</SectionTitle>
          <div className={s.stack}>
            {involved.slice(0, limit).map((c) => (
              <MatchRow key={c.matchId} card={c} compact onOpen={opener.open} onAction={opener.act} />
            ))}
          </div>
        </section>
      )}

      {form && <ItemForm kind={form.kind} contactId={contactId} need={form.need} service={form.service} open forLabel={name} onClose={() => setForm(null)} />}
      <CompleteNeedDialog need={completing} open={Boolean(completing)} onClose={() => setCompleting(null)} />
      <MatchModal card={opener.modal?.card ?? null} initialAction={opener.modal?.action ?? null} onClose={opener.close} />
    </div>
  );
}
